import { create_send_order, delete_send_order, query_send_order_list } from '@/api/jms-model';
import { TableColumn } from '@/typing';
import { SearchConfig, Record } from '@/components/page-model/typing';
import ls from '@/utils/local-storage';
import { STORAGE_CURRENT_ORG_KEY } from '@/store/mutation-type';
import {
  slotList,
  btn_list,
  head_btn_list,
  event_obj,
  config,
} from '@/components/page-model/jms/jms-sendorder-config';

import { reactive } from 'vue';
import { get_is_active, getDateTime, toFixed0 } from '@/utils/function';

const None = null;
const True = true;
const False = false;

const current_org = ls.get(STORAGE_CURRENT_ORG_KEY); // 当前组织org

const search_config: SearchConfig = {
  settings: {
    labelAlign: 'right',
    layout: 'inline',
  },
  fields: [
    {
      type: 'input',
      name: 'code',
      label: '发货单号',
      disabled: false,
      allowClear: true,
      inputType: 'text',
      defaultValue: '',
      placeholder: '请填写发货单号',
    },
    {
      type: 'remotemodal',
      name: 'order_id',
      label: '隶属订单',
      disabled: false,
      placeholder: '请选择隶属订单',
      datasourceType: 'remote',
      mode: 'default',
      allowClear: true,
      modalType: 'jms-productorder',
    },
    {
      type: 'remotemodal',
      name: 'franchisee_id',
      label: '加盟商',
      disabled: false,
      placeholder: '请选择加盟商',
      datasourceType: 'remote',
      mode: 'default',
      allowClear: true,
      modalType: 'jms-franchisee',
    },
    {
      type: 'remotemodal',
      name: 'product_id',
      label: '产品',
      disabled: false,
      placeholder: '请选择产品',
      datasourceType: 'remote',
      mode: 'default',
      allowClear: true,
      modalType: 'jms-product',
    },
    {
      type: 'rangepicker',
      name: 'send_date',
      label: '发货时间',
      disabled: false,
      allowClear: true,
      inputType: 'number',
      defaultValue: '',
      showTime: false,
      placeholder: '请填写发货时间',
    },
    {
      type: 'rangepicker',
      name: 'check_date',
      label: '签收时间',
      disabled: false,
      allowClear: true,
      inputType: 'number',
      defaultValue: '',
      showTime: false,
      placeholder: '请填写签收时间',
    },
    {
      type: 'input',
      name: 'company',
      label: '物流公司',
      disabled: false,
      allowClear: true,
      inputType: 'text',
      defaultValue: '',
      placeholder: '请填写物流公司',
    },
    {
      type: 'input',
      name: 'company_no',
      label: '物流单号',
      disabled: false,
      allowClear: true,
      inputType: 'text',
      defaultValue: '',
      placeholder: '请填写物流单号',
    },
  ],
  rules: {},
  model: reactive({
    org_id: current_org?.id,
    is_active: true,
    code: null,
    order_id: null,
    franchisee_id: null,
    product_id: null,
    send_date: null,
    check_date: null,
    company: null,
    company_no: null,
  }),
};
const search_options = {
  is_active: [
    { value: None, name: '全部' },
    { value: True, name: '可用' },
    { value: False, name: '作废' },
  ],
};

const edit_model_config: SearchConfig = {
  settings: {
    labelAlign: 'right',
    layout: 'horizontal',
    col: config.creatCol,
  },
  fields: [
    {
      type: 'remotemodal',
      name: 'order_id',
      label: '隶属订单',
      disabled: false,
      placeholder: '请选择隶属订单',
      datasourceType: 'remote',
      mode: 'default',
      allowClear: true,
      modalType: 'jms-productorder',
    },
    {
      type: 'input',
      name: 'num',
      label: '产品数量',
      disabled: false,
      allowClear: true,
      inputType: 'number',
      defaultValue: '',
      placeholder: '请填写产品数量',
    },
  ],
  rules: { order_id: [{ required: true, message: '请填输入隶属订单' }] },
  model: reactive({
    org_id: current_org?.id,
    is_active: true,
    order_id: null,
    num: null,
  }),
};
const edit_model_config_options = {};

const update_model_config: SearchConfig = {
  settings: {
    labelAlign: 'right',
    layout: 'horizontal',
    col: config.creatCol,
  },
  fields: [],
  rules: {},
  model: reactive({
    org_id: current_org?.id,
    is_active: true,
  }),
};
const update_model_config_options = {};

const titleList = {
  title: '发货单管理',
  addModalTitle: '新建发货单',
  updateTitle: '修改发货单',
  is_create: false, // 是否有【新建】按钮
  is_update: false, // 是否有【修改】按钮
  is_remove: false, // 是否有【删除】按钮
  is_recover: false, // 是否有【恢复】按钮
};

const api_fields: Record = {
  update: {
    id: 'id',
  },
  delete: {
    sendorder_id: 'id',
  },
  recover: {
    id: 'id',
  },
  updateData: {
    org_id: 'org_id',
    id: 'id',
  },
};

const baseColumns: TableColumn[] = [
  {
    title: '序号',
    dataIndex: 'index',
    width: 50,
    fixed: 'left',
  },
  {
    title: '发货单号',
    width: 150,
    dataIndex: 'code',
    fixed: 'left',
    checked: true,
  },
  {
    title: '订单单号',
    width: 150,
    fixed: 'left',
    dataIndex: 'order__code',
    checked: true,
  },
  {
    title: '加盟商ID',
    width: 150,
    dataIndex: 'franchisee_id',
    checked: false,
  },
  {
    title: '加盟商',
    width: 150,
    dataIndex: 'franchisee__name',
    checked: false,
  },
  {
    title: '产品编号',
    width: 150,
    dataIndex: 'product__code',
    checked: true,
  },
  {
    title: '产品名称',
    width: 150,
    dataIndex: 'product__name',
    checked: true,
  },
  {
    title: '产品数量',
    width: 150,
    dataIndex: 'num',
    checked: true,
  },
  {
    title: '产品单价',
    width: 150,
    dataIndex: 'price',
    checked: true,
    content: (record: any, text: any) => {
      return toFixed0(text);
    },
  },
  {
    title: '产品金额',
    width: 150,
    dataIndex: 'total_price',
    checked: true,
    content: (record: any, text: any) => {
      return toFixed0(text);
    },
  },
  {
    title: '状态',
    width: 100,
    dataIndex: 'status',
    checked: true,
    content: (record: any, text: any) => {
      let content = '';
      switch (text) {
        case 0:
          content = '待发货';
          break;
        case 2:
          content = '已发货';
          break;
        case 3:
          content = '确认签收';
          break;
        default:
          content = '';
          break;
      }
      return content;
    },
    filterMultiple: false,
    filters: [
      { text: '全部', value: '' },
      { text: '待发货', value: '0' },
      { text: '已发货', value: '2' },
      { text: '确认签收', value: '3' },
    ],
  },
  {
    title: '发货时间',
    width: 150,
    dataIndex: 'send_date',
    checked: true,
    content: (record: any, text: any) => {
      return getDateTime(text);
    },
  },
  {
    title: '创建日期',
    width: 150,
    dataIndex: 'create_time',
    checked: true,
    content: (record: any, text: any) => {
      return getDateTime(text);
    },
  },
  {
    title: '物流公司',
    width: 150,
    dataIndex: 'company',
    checked: true,
  },
  {
    title: '物流单号',
    width: 150,
    dataIndex: 'company_no',
    checked: true,
  },
  {
    title: '签收日期',
    width: 150,
    dataIndex: 'check_date',
    checked: true,
    content: (record: any, text: any) => {
      return getDateTime(text);
    },
  },
  {
    title: '备注',
    width: 150,
    dataIndex: 'mark',
    checked: true,
  },
  // 不显示字段
  {
    title: 'ID',
    width: 150,
    dataIndex: 'id',
    checked: false,
  },
  {
    title: '产品订单ID',
    width: 150,
    dataIndex: 'order_id',
    checked: false,
  },
  {
    title: '产品ID',
    width: 150,
    dataIndex: 'product_id',
    checked: false,
  },
  {
    title: '更新时间',
    width: 150,
    dataIndex: 'update_time',
    checked: false,
    content: (record: any, text: any) => {
      return getDateTime(text);
    },
  },
  {
    title: '是否可用',
    width: 150,
    dataIndex: 'is_active',
    checked: false,
    cellComponent: 'boolean',
    name1: '可用',
    name2: '禁用',
    filterMultiple: false,
    filters: [
      { text: '全部', value: '' },
      { text: '可用', value: true },
      { text: '作废', value: false },
    ],
    content: (record: any, text: any) => {
      return get_is_active(text);
    },
  },
  {
    title: '操作',
    width: 200,
    dataIndex: 'action',
    checked: true,
    cellComponent: 'action',
    fixed: 'right',
  },
];
const newSlotList: any = [];
Object.assign(newSlotList, slotList);
newSlotList.push(
  { type: 'custom', slotName: 'status', field_0: '待发货', field_2: '已发货', field_3: '确认签收' },
  { type: 'custom', field_true: '可用', field_false: '作废' },
);

export default () => {
  return {
    searchConfig: search_config,
    searchConfigOptions: search_options,
    formModalConfig: edit_model_config,
    formModalConfigOptions: edit_model_config_options,
    updateFormModalConfig: update_model_config,
    updateFormModalConfigOptions: update_model_config_options,
    queryData: query_send_order_list,
    addData: create_send_order,
    updateData: None,
    removeData: delete_send_order,
    recoverData: None,
    baseColumns: baseColumns,
    slotList: newSlotList,
    titleList: titleList,
    api_fields: api_fields,
    btn_list: btn_list,
    head_btn_list: head_btn_list,
    event_obj: event_obj,
  };
};
